import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Snackbar,
    Alert,
    useTheme,
    styled,
    Avatar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Card } from "../../components/Card";
import useAuth from "../../context";
import API_ENDPOINTS from "../../apis/endpoints";
import { useParams, useNavigate } from "react-router-dom";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import useSWR from "swr";

const AudioMessage = ({ url, filename }) => {
    const theme = useTheme();
    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                // bgcolor: "#f8f9fa",
                borderRadius: "8px",
                width: "fit-content",

                pl: 2,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    backgroundColor: theme.palette.primary[50],
                    borderRadius: "5%",
                    padding: 1,
                }}
            >
                <Typography variant="caption" sx={{ color: "#666" }}>
                    {filename}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <audio
                        controls
                        style={{
                            height: "32px",
                            width: "300px",
                            backgroundColor: "#E9E7F1 !important",
                        }}
                    >
                        <source src={url} type="audio/mp3" />
                    </audio>
                </Box>
            </Box>
            <Box
                onClick={handleDownload}
                sx={{
                    backgroundColor: theme.palette.primary[50],
                    borderRadius: "10%",
                    width: "4rem",
                    height: "4.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                }}
            >
                <DownloadForOfflineIcon
                    sx={{
                        height: "2rem",
                        width: "2rem",
                        color: theme.palette.primary[500],
                    }}
                />
            </Box>
        </Box>
    );
};

const VoiceCloningChat = ({ onBack }) => {
    const { modelId } = useParams();
    const navigate = useNavigate();
    const storedDeviceId = localStorage.getItem("device-id");
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [oldestLoadedId, setOldestLoadedId] = useState(null);
    const [modelName, setModelName] = useState("");
    const [emotion, setEmotion] = useState("neutral");
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const scrollRef = useRef(null);
    const loadTriggerRef = useRef(null);
    const auth = useAuth();

    const { data } = useSWR(`${API_ENDPOINTS.AI_MODEL_VOICE}${modelId}/`);

    const fetchChatMessages = useCallback(
        async (before = null) => {
            setLoading(true);
            try {
                const url = `${
                    API_ENDPOINTS.AI_MODEL_VOICE
                }${modelId}/chat/?limit=30${before ? `&before=${before}` : ""}`;
                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                });
                if (!response.ok)
                    throw new Error("Failed to fetch chat messages");
                const data = await response.json();
                if (data.data.messages.length < 10) {
                    setHasMoreMessages(false);
                }
                return data.data.messages.reverse();
            } catch (error) {
                console.error("Error fetching chat messages:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to fetch messages. Please try again.",
                });
                return [];
            } finally {
                setLoading(false);
            }
        },
        [modelId, auth.accessToken, storedDeviceId]
    );

    const loadMoreMessages = useCallback(async () => {
        if (loading || !oldestLoadedId || !hasMoreMessages) return;
        const newMessages = await fetchChatMessages(oldestLoadedId);
        if (newMessages.length > 0) {
            setMessages((prev) => [...prev, ...newMessages]);
            setOldestLoadedId(newMessages[newMessages.length - 1].this_id);
        }
    }, [loading, oldestLoadedId, fetchChatMessages, hasMoreMessages]);

    useEffect(() => {
        const loadInitialMessages = async () => {
            const initialMessages = await fetchChatMessages();
            setMessages(initialMessages);
            if (initialMessages.length > 0) {
                setOldestLoadedId(
                    initialMessages[initialMessages.length - 1].this_id
                );
            }
        };
        loadInitialMessages();
    }, [fetchChatMessages]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreMessages();
                }
            },
            { threshold: 1.0 }
        );
        if (loadTriggerRef.current) {
            observer.observe(loadTriggerRef.current);
        }
        return () => observer.disconnect();
    }, [loadMoreMessages]);

    const handleSendMessage = async () => {
        if (newMessage.trim()) {
            try {
                setLoading(true);
                const response = await fetch(
                    `${API_ENDPOINTS.AI_MODEL_VOICE}${modelId}/chat/`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            "x-cc-device-id": storedDeviceId,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            message: newMessage,
                            emotion: emotion,
                        }),
                    }
                );
                if (!response.ok) {
                    throw new Error(
                        `Failed to send message: ${response.statusText}`
                    );
                }
                const data = await response.json();
                const newMessageId = data.data.id;
                setMessages((prev) => [data.data, ...prev]);
                setNewMessage("");

                const pollStatus = async () => {
                    try {
                        const statusResponse = await fetch(
                            `${API_ENDPOINTS.AI_MODEL_VOICE}${modelId}/chat/${newMessageId}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${auth.accessToken}`,
                                    "x-cc-device-id": storedDeviceId,
                                },
                            }
                        );
                        if (!statusResponse.ok) {
                            throw new Error(
                                `Failed to fetch status: ${statusResponse.statusText}`
                            );
                        }
                        const statusData = await statusResponse.json();
                        if (statusData.data.status === "READY") {
                            setMessages((prev) =>
                                prev.map((msg) =>
                                    msg.id === newMessageId
                                        ? statusData.data
                                        : msg
                                )
                            );
                        } else if (
                            ["PENDING", "PROCESSING"].includes(
                                statusData.data.status
                            )
                        ) {
                            setTimeout(pollStatus, 1000);
                        } else {
                            throw new Error(
                                `Unexpected status: ${statusData.data.status}`
                            );
                        }
                    } catch (error) {
                        console.error("Error polling status:", error);
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === newMessageId
                                    ? {
                                          ...msg,
                                          status: "ERROR",
                                          error: error.message,
                                      }
                                    : msg
                            )
                        );
                    }
                };

                pollStatus();
            } catch (error) {
                console.error("Error sending message:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to send message. Please try again.",
                });
                setMessages((prev) => [
                    {
                        id: Date.now(),
                        request: newMessage,
                        status: "ERROR",
                        error: error.message,
                    },
                    ...prev,
                ]);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    return (
        <>
            <div
                style={{
                    display: "flex",
                    alignContent: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    gap: "1rem",
                }}
            >
                <ArrowBackIosNewRoundedIcon
                    onClick={() => navigate("list")}
                    sx={{ left: "10px" }}
                />
                <Typography variant="featureAccent">{data?.name}</Typography>
            </div>
            <Card
                sx={{
                    width: "90vw",
                    maxHeight: "90vh",
                    margin: "auto",
                    marginTop: "50px",
                }}
            >
                <Box
                    sx={{
                        height: "84vh",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        {[...messages].map((message) => (
                            <Box
                                key={message.id}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: message.response
                                        ? "flex-start"
                                        : "flex-end",
                                    p: 1,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-end",

                                        gap: 2,
                                        marginRight: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: "50vw",
                                            bgcolor:
                                                message.status === "ERROR"
                                                    ? "#ffcccb"
                                                    : message.response
                                                      ? "#f1f0f0"
                                                      : "#007bff",
                                            color:
                                                message.status === "ERROR"
                                                    ? "red"
                                                    : message.response
                                                      ? "black"
                                                      : "white",
                                            borderRadius: 2,
                                            p: 1,
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {message.response
                                                ? ` ${message.response}`
                                                : ` ${message.prompt}`}
                                        </Typography>
                                        {message.status === "PENDING" && (
                                            <Typography variant="caption">
                                                Processing...
                                            </Typography>
                                        )}
                                        {message.status === "ERROR" && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                Error: {message.error}
                                            </Typography>
                                        )}
                                    </Box>
                                </div>
                                {message.attachments &&
                                    message.attachments.length > 0 && (
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                // alignItems: "flex-start",
                                                gap: "10px",
                                                justifyContent: "flex-start",
                                                width: "100%",
                                            }}
                                        >
                                            <AudioMessage
                                                url={message.attachments[0].url}
                                                filename={
                                                    message.attachments[0].name
                                                }
                                            />
                                        </div>
                                    )}
                            </Box>
                        ))}

                        <div ref={loadTriggerRef} style={{ height: "1px" }}>
                            {loading && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </div>
                    </Box>
                    <Box sx={{ display: "flex", p: 2, alignItems: "center" }}>
                        <FormControl sx={{ minWidth: 120, mr: 2 }}>
                            <InputLabel>Emotion</InputLabel>
                            <Select
                                value={emotion}
                                onChange={(e) => setEmotion(e.target.value)}
                                label="Emotion"
                            >
                                {/* Populate emotions from the API response */}
                                {data &&
                                    data?.metadata?.emotions?.map(
                                        (emotionValue) => (
                                            <MenuItem
                                                key={emotionValue}
                                                value={emotionValue}
                                            >
                                                {emotionValue
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    emotionValue.slice(1)}{" "}
                                                {/* Capitalize first letter */}
                                            </MenuItem>
                                        )
                                    )}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Type a command..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") handleSendMessage();
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<SendIcon />}
                            onClick={handleSendMessage}
                            sx={{ ml: 1 }}
                        >
                            Send
                        </Button>
                    </Box>
                </Box>
            </Card>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ open: false, message: "" })}
            >
                <Alert
                    onClose={() => setSnackbar({ open: false, message: "" })}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default VoiceCloningChat;
